import {
	computed,
	defineComponent,
	type PropType,
} from 'vue'

import type { CAInputMessage } from '@ds/interfaces'
import { sanitizeHtml } from '@vuejs/utils/stringUtils'

import CAIcon from '../shared/CAIcon'

const MESSAGE_ICONS: Partial<Record<CAInputMessage['type'], string>> = {
	error: 'SVGAlertFilled',
	success: 'SVGCheckCircleFilled',
}

/**
 * The validation message shown beneath a form element.
 *
 * @prop {string} blockClass - The BEM block the message belongs to, e.g. `ca-input`.
 * @prop {string} [dataTest=''] - Data attribute for testing.
 * @prop {string} [id=''] - Id for the message, referenced by the field's `aria-describedby`.
 * @prop {CAInputMessage | null} [message=null] - The message to display.
 * @prop {boolean} [showIcon=false] - Whether the message displays a status icon.
 */
export default defineComponent({
	name: 'CAFormMessage',

	props: {
		/**
		 * The BEM block the message belongs to, e.g. `ca-input`.
		 * @type {string}
		 */
		blockClass: { required: true, type: String },
		/**
		 * Data attribute for testing.
		 * @default ''
		 * @type {string}
		 */
		dataTest: { default: '', type: String },
		/**
		 * Id for the message, referenced by the field's `aria-describedby`.
		 * @default ''
		 * @type {string}
		 */
		id: { default: '', type: String },
		/**
		 * The message to display.
		 * @default null
		 * @type {CAInputMessage | null}
		 */
		message: { default: null, type: Object as PropType<CAInputMessage | null> },
		/**
		 * Whether the message displays a status icon.
		 * @default false
		 * @type {boolean}
		 */
		showIcon: { default: false, type: Boolean },
	},

	setup(props) {
		const icon = computed(() => {
			if (!props.showIcon || !props.message) {
				return undefined
			}

			return MESSAGE_ICONS[props.message.type]
		})

		return {
			icon,
		}
	},

	render() {
		if (!this.message?.text) {
			return null
		}

		const { blockClass } = this

		return (
			<p
				class={`${blockClass}__message ${blockClass}__message--${this.message.type}`}
				data-test={this.dataTest || undefined}
				id={this.id || undefined}
				role={this.message.type === 'error' ? 'alert' : 'status'}
			>
				{this.icon &&
					<CAIcon
						aria-hidden="true"
						class={`${blockClass}__message-icon`}
						data-test={this.dataTest ? `${this.dataTest}-icon` : undefined}
						icon={this.icon}
					/>
				}

				<span innerHTML={sanitizeHtml(this.message.text)} />
			</p>
		)
	},
})
